export function workspacesNamespace(http) {
    return {
        /**
         * List workspaces → unwraps to `{ workspaces }`.
         *
         * `includeSystem` asks for the machine-managed ones too (AITY review
         * batches, vault writers). The server honours it only for org admins and
         * above, and silently ignores it otherwise, so a caller can pass it
         * without first knowing the viewer's role.
         */
        list(opts) {
            return http.get('/workspaces', {
                query: opts?.includeSystem ? { include_system: 1 } : undefined,
                signal: opts?.signal,
            });
        },
        /** Workspace catalogue (bare `{ data, facets, ... }` passthrough). */
        catalogue(workspaceId, params = {}) {
            const { facets, signal, ...query } = params;
            return http.get(`/workspaces/${workspaceId}/catalogue`, { query, facets, signal });
        },
        /** Attach a resource to a workspace. Throws on failure. */
        addResource(workspaceId, resourceId) {
            return http.post(`/workspaces/${workspaceId}/resources`, { body: { resource_id: resourceId } });
        },
        /** Detach a resource from a workspace. Throws on failure. */
        removeResource(workspaceId, resourceId) {
            return http.delete(`/workspaces/${workspaceId}/resources/${resourceId}`);
        },
        /**
         * Attach many resources at once (max 200). Because workspace membership is
         * what a vault projects, this is also the bulk publish gesture.
         *
         * Resolves with a partial-success report; throws only when not one id
         * could be applied. `raw` keeps the report's fields, which sit beside
         * `success` rather than under `data`.
         */
        bulkAttachResources(workspaceId, resourceIds) {
            return http.post(`/workspaces/${workspaceId}/resources/bulk-attach`, {
                body: { resource_ids: resourceIds },
                raw: true,
            });
        },
        /** Detach many resources at once (max 200). See `bulkAttachResources`. */
        bulkDetachResources(workspaceId, resourceIds) {
            return http.post(`/workspaces/${workspaceId}/resources/bulk-detach`, {
                body: { resource_ids: resourceIds },
                raw: true,
            });
        },
        /** Create a workspace → unwraps to `{ workspace }`. */
        create(body) {
            return http.post('/workspaces', { body });
        },
        /** Update a workspace → unwraps to `{ workspace }`. */
        update(workspaceId, body) {
            return http.put(`/workspaces/${workspaceId}`, { body });
        },
        /** Delete a workspace (also used to delete an AITY batch). Throws on failure. */
        delete(workspaceId) {
            return http.delete(`/workspaces/${workspaceId}`);
        },
        /** AITY batches (raw — keeps the `meta.pagination` sibling). */
        aityBatches(page = 1, perPage = 50) {
            return http.get('/workspaces/aity-batches', { query: { page, per_page: perPage }, raw: true });
        },
        /** AITY status for a workspace → unwraps `data`. */
        aityStatus(workspaceId) {
            return http.get(`/workspaces/${workspaceId}/aity-status`);
        },
        /** RAG ask over a workspace (raw — caller wants the whole `{ success, data }`). */
        ask(workspaceId, body) {
            return http.post(`/workspaces/${workspaceId}/ask`, { body, raw: true });
        },
        /** Mark an AITY batch reviewed. Throws on failure. */
        markReviewed(workspaceId) {
            return http.post(`/workspaces/${workspaceId}/mark-reviewed`);
        },
    };
}
//# sourceMappingURL=workspaces.js.map